// Fleet management API — raw fetch against the fleetd routes the console
// reverse-proxies (api/internal/api/rest/fleet_proxy.go): read endpoints under
// /api/fleet*, mutations behind the same admin auth. These routes are NOT in
// openapi.yaml (fleetd is a loopback sidecar, proxied — not a typed Go handler),
// so this module mirrors FleetPage's existing raw-fetch + localStorage-token
// pattern rather than openapi-fetch. The proxy group deliberately has NO
// idempotency middleware, so mutations carry no Idempotency-Key.
//
// Long ops (add / resize / remove / sso activate) return { job_id } immediately;
// poll getJob(id) until state is "success" | "failed".

const BASE = import.meta.env.BASE_URL + "api";

function authHeaders(extra?: Record<string, string>): Record<string, string> {
  const tok = localStorage.getItem("bluerock.token");
  return { ...(tok ? { Authorization: `Bearer ${tok}` } : {}), ...(extra ?? {}) };
}

async function detail(res: Response, action: string): Promise<string> {
  try {
    const body = (await res.json()) as { error?: string; detail?: string; title?: string };
    if (typeof body.error === "string") return body.error;
    if (typeof body.detail === "string") return body.detail;
    if (typeof body.title === "string") return body.title;
  } catch {
    // not JSON — fall through
  }
  return `${action} failed (${res.status})`;
}

export interface Size {
  size: string;
  vcpu: number;
  memGiB: number;
  /** Recommended sandbox capacity for this instance size (fleetd derives it from
   *  RAM, ~2 GiB/sandbox). The add-host modal prefills it; the operator can override. */
  sandboxes?: number;
}

// Curated t-shirt sizes matching this fleet's real Azure VM family
// (Das_v7). fleetd's /fleet/sizes enumeration path is AWS-only and
// returns [] unconditionally on an Azure-backed fleet — see
// fleetApi.sizes() below, which tries the real endpoint first and
// falls back to this list. Single source of truth for both the
// Add-host/Resize picker (useFleetOps.tsx) and the size-derived
// sandbox-capacity display (FleetPage.tsx) — they must never drift.
export const CURATED_SIZES: Size[] = [
  { size: "small", vcpu: 2, memGiB: 8, sandboxes: 4 },
  { size: "medium", vcpu: 4, memGiB: 16, sandboxes: 8 },
  { size: "large", vcpu: 8, memGiB: 32, sandboxes: 16 },
];

export const SIZE_LABEL: Record<string, string> = { small: "Small", medium: "Medium", large: "Large" };

// Candidate Azure VM types per size, best first. The operator picks the
// exact type; with fallback on, add-node tries the chosen type then the
// rest (first the region offers wins); with fallback off, only the
// chosen type (fails if unavailable in the region).
export const SIZE_CANDIDATES: Record<string, string[]> = {
  small: ["Standard_D2as_v7", "Standard_D2as_v5", "Standard_D2s_v5"],
  medium: ["Standard_D4as_v7", "Standard_D4as_v5", "Standard_D4s_v5"],
  large: ["Standard_D8as_v7", "Standard_D8as_v5", "Standard_D8s_v5"],
};

export interface FleetConfig {
  max_nodes: number;
  max_sandboxes_per_node: number;
  default_size?: string;
  location?: string;
  vm_size?: string;
  [k: string]: unknown;
}

export interface SsoConfig {
  provider: string;
  tenant: string;
  provisioner: string;
  protocol: string;
  issuer?: string;
  client_id?: string;
  domains?: string;
  has_client_secret: boolean;
  active: boolean;
}

export interface Provider {
  handle: string;
  name: string;
  kind: string;
  issuer: string;
  client_id: string;
  admins: string;
  domains: string;
  default_dur: string;
  max_dur: string;
  has_client_secret: boolean;
  registered: boolean;
  /** True when the provisioner lives in the CA's ca.json but has no console-managed
   *  env file (configured directly during host setup). Surfaced read-only: the console
   *  can't see its secret, and editing/removing it would restart step-ca, so the UI
   *  disables those on external providers. */
  external?: boolean;
}

export interface ProviderInput {
  handle?: string;
  name?: string;
  kind?: string;
  tenant?: string;
  issuer_url?: string;
  client_id?: string;
  client_secret?: string;
  admins?: string;
  domains?: string;
  default_dur?: string;
  max_dur?: string;
}

// A fleet user — learned from CA SSO logins and/or ssh-key grants (fleetd registry).
// Display-only; enforcement stays in the node trust files. last_login for SSO users is
// their latest cert issuance ("last seen in the CA window"); for key grants, grant time.
export interface FleetUser {
  identity: string;
  display: string;
  owner_kind: "human" | "agent" | "unknown";
  source: "sso" | "ssh-key";
  first_seen: string;
  last_login: string;
  access: "active" | "revoked" | "unknown";
  sandbox_count: number;
  tier?: string;
  associated_human?: string | null; // agent users: the human operator, if given
}

// A derived sandbox-provisioning issue (fleetd /api/fleet/issues). Read-only diagnostics.
// `status: "indeterminate"` means we are BLIND (stale scan / degraded registry / unreachable
// host) — the condition can be neither confirmed nor cleared; render it as such, never as
// healthy. Issues auto-appear and auto-clear; there is no stored/dismissed state in v1.
export type IssueKind =
  | "sso-no-sandbox" | "sso-no-sandbox-stale" | "issued-not-authorized"
  | "host-not-enforcing" | "sandbox-idle-no-client"
  | "scan-stale" | "registry-degraded" | "host-unreachable";
export type IssueSeverity = "danger" | "warning" | "info";
export interface FleetIssue {
  id: string;
  kind: IssueKind;
  severity: IssueSeverity;
  subject: { type: "user" | "host" | "system"; id: string; display: string };
  host: string | null;
  first_detected: string;
  last_seen: string;
  status: "open" | "indeterminate";
  evidence: Record<string, unknown>;
}
export interface FleetIssues {
  generated_at: string;
  scan_age_s: number | null;
  scan_ok: boolean;
  registry_ok: boolean;
  issues: FleetIssue[];
}

// Bluedev image catalog (fleetd GET /api/fleet/images). Read-only inventory: the
// primed images on the worker, the authoring catalog templates, and the launch
// allow-list. No mutation/build in v1.
export interface FleetImage {
  id: string;        // bluedev-primed/<name>
  name: string;      // <name>
  tag: string;
  size: string;      // human, e.g. "5.38GB"
  created: string;   // human, e.g. "5 weeks ago"
  image_id: string;
}
export interface FleetCatalogEntry {
  id: string;        // catalog template id
  primed: boolean;   // a bluedev-primed image exists for it
  launchable: boolean; // matches the catalog-allow.list glob
}
export interface FleetImages {
  images: FleetImage[];
  catalog: FleetCatalogEntry[];
  allow_list: string[];
  default_image: string; // per-host default catalog id (advisory in v1)
}

// An ACR image the admin has registered for the catalog, by immutable digest. RECORD
// ONLY today: registering does NOT pull, launch, or allow-list the image — admission
// (pull + enforcement verification) is a separate, gated step. `state` reflects that:
// "registered" = recorded, awaiting verification; never implies the image is enforcing.
export interface AcrRegistration {
  id: string;         // catalog id it will occupy, e.g. team-web
  registry: string;   // <name>.azurecr.io
  repository: string; // repo/path
  digest: string;     // sha256:...
  note?: string;
  state: "registered" | "verifying" | "verified";
  registered_by: string;
  registered_at: string;
}
export interface AcrRegistrationInput {
  id: string;
  registry: string;
  repository: string;
  digest: string;
  note?: string;
}

// Live enforcement-attestation verdicts (docker-attester). Per-signal states are honest:
// binding is "unverified" on Docker until the bru shim-attestation interface lands, so a
// live container reads "partial", never a false "enforcing". A dead attester → stale.
export interface AttestationVerdict {
  container_id: string;
  name: string;
  tier: string;
  state: "enforcing" | "partial" | "control-failure" | "stale" | "unattested" | "defeated";
  reason: string;
  binding: "verified" | "unverified";
  pipeline: "verified" | "stale" | "unattested";
  mcp: "verified" | "unattested";
  policy_hash_ok: boolean | null;
}
export interface Attestation {
  available: boolean;
  stale?: boolean;
  heartbeat?: string;
  loop_ok?: boolean;
  verdicts: AttestationVerdict[];
}

export type ControlStatus = "enforcing" | "available" | "attention" | "partial";
export interface ContainmentControl { name: string; plain: string; detail?: string; status: ControlStatus }
export interface ContainmentLayer { id: string; name: string; engine: string; plain: string; detail?: string; controls: ContainmentControl[] }
export interface ImdsHost { host: string; http_tokens: string; hop_limit: number; endpoint: string; status: "hardened" | "partial" | "exposed" }
export interface Containment {
  layers: ContainmentLayer[];
  imds: ImdsHost[];
  summary: Record<string, number>;
}

// Access grants — who may reach a braced sandbox on a node. An ssh-key grant is a
// bearer credential (principal = key:SHA256:<fp>); an oidc-cert grant pre-authorizes
// an email / *@domain proven later by SSO. See fleetd /api/fleet/access + accessctl.
export interface AccessGrant {
  principal: string;
  owner: string;
  tier: string | null;
  kind: "ssh-key" | "oidc-cert";
}
export interface GrantInput {
  mode: "ssh-key" | "oidc-cert";
  tier: string;
  node?: string;
  owner?: string;   // ssh-key: required label bound to the key fingerprint
  owner_kind?: "human" | "agent"; // operator-asserted; surfaced on the Users page
  associated_human?: string; // agent only: the human who operates it (optional)
  pubkey?: string;  // ssh-key: the developer's PUBLIC key
  principal?: string; // oidc-cert: email or *@domain
}

export interface GrantResult {
  ok: boolean; node: string; node_name?: string; tier: string; mode: string;
  owner?: string | null; principal?: string | null; fingerprint?: string | null;
}

export interface Job {
  id: string;
  label: string;
  state: "queued" | "running" | "success" | "failed";
  // A job may emit a machine-parseable `RESULT:` line that fleetd captures here.
  // identityctl uses it so the console can tell a SAFE rollback (previous
  // sign-in config restored) apart from a hard failure — the exit code alone
  // (success/failed) can't express that. null for jobs that don't emit one.
  result: "ok" | "rolled_back" | "failed" | null;
  exit: number | null;
  started: number;
  finished: number | null;
  log: string[];
}

async function get<T>(path: string, action: string): Promise<T> {
  const res = await fetch(`${BASE}${path}`, { cache: "no-store", headers: authHeaders() });
  if (!res.ok) throw new Error(await detail(res, action));
  return (await res.json()) as T;
}

async function mutate(path: string, method: string, body?: unknown, action = "Request"): Promise<Response> {
  const res = await fetch(`${BASE}${path}`, {
    method,
    headers: authHeaders(body !== undefined ? { "Content-Type": "application/json" } : undefined),
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) throw new Error(await detail(res, action));
  return res;
}

export const fleetApi = {
  // Tries the real endpoint first (so a fleet where size enumeration
  // actually works gets real data); falls back to the curated list —
  // which matches this fleet's real VM family — on an empty result or
  // a request failure, so the picker never shows a dead spinner.
  sizes: async (): Promise<Size[]> => {
    try {
      const real = await get<{ sizes: Size[] }>("/fleet/sizes", "Load sizes").then((r) => r.sizes ?? []);
      return real.length ? real : CURATED_SIZES;
    } catch {
      return CURATED_SIZES;
    }
  },
  config: () => get<FleetConfig>("/fleet/config", "Load config"),
  // A 404 means this node's fleetd build predates the user registry route — there
  // is simply no SSO/SSH-key roster to show, so render the empty state rather than
  // an error. Other failures still surface.
  users: async (): Promise<FleetUser[]> => {
    const res = await fetch(`${BASE}/fleet/users`, { cache: "no-store", headers: authHeaders() });
    if (res.status === 404) return [];
    if (!res.ok) throw new Error(await detail(res, "Load users"));
    return ((await res.json()) as { users: FleetUser[] }).users ?? [];
  },
  // Bluedev image catalog (read-only): primed images + authoring templates + launch allow-list.
  images: () => get<FleetImages>("/fleet/images", "Load images"),
  // Write the bouncer launch allow-list (enforced). Returns the refreshed catalog.
  setCatalogAllow: (allow_list: string[]) =>
    mutate("/fleet/catalog-allow", "PUT", { allow_list }, "Save allow-list").then((r) => r.json() as Promise<FleetImages>),
  // Set the per-host default catalog id (advisory in v1). "" clears it.
  setDefaultImage: (def: string) =>
    mutate("/fleet/default-image", "PUT", { default: def }, "Set default image").then((r) => r.json() as Promise<FleetImages>),
  // ACR image registrations (record-only). List / add a customer ACR image by digest.
  acrRegistrations: () =>
    get<{ registrations: AcrRegistration[] }>("/fleet/images/registrations", "Load ACR registrations").then((r) => r.registrations ?? []),
  registerAcrImage: (body: AcrRegistrationInput) =>
    mutate("/fleet/images/registrations", "POST", body, "Register ACR image").then((r) => r.json() as Promise<AcrRegistration>),
  // Live enforcement-attestation verdicts from the docker-attester (read-only).
  attestation: () => get<Attestation>("/fleet/attestation", "Load attestation"),

  // Derived sandbox diagnostics. Returns the freshness flags alongside the list so the UI
  // can show "degraded/unknown" honestly instead of a false "no issues" when we're blind.
  issues: () => get<FleetIssues>("/fleet/issues", "Load issues"),
  sso: () => get<SsoConfig>("/fleet/sso", "Load SSO"),
  jobs: () => get<{ jobs: Job[] }>("/fleet/jobs", "Load jobs").then((r) => r.jobs ?? []),
  job: (id: string) => get<Job>(`/fleet/jobs/${encodeURIComponent(id)}`, "Load job"),

  addNode: (opts: { name?: string; size?: string }) =>
    mutate("/fleet/nodes", "POST", opts, "Add host").then((r) => r.json() as Promise<{ job_id: string }>),
  resizeNode: (name: string, size: string) =>
    mutate(`/fleet/nodes/${encodeURIComponent(name)}/resize`, "POST", { size }, "Resize host").then(
      (r) => r.json() as Promise<{ job_id: string }>,
    ),
  removeNode: (name: string) =>
    mutate(`/fleet/nodes/${encodeURIComponent(name)}`, "DELETE", undefined, "Remove host").then(
      (r) => r.json() as Promise<{ job_id: string }>,
    ),

  putConfig: (patch: Partial<Pick<FleetConfig, "max_nodes" | "max_sandboxes_per_node" | "default_size">>) =>
    mutate("/fleet/config", "PUT", patch, "Save config").then((r) => r.json() as Promise<FleetConfig>),
  // NOTE: identity/SSO is configured through the PROVIDER flow (providers() /
  // addProvider() / updateProvider()), which the Identity page uses. The old
  // putSso()/activateSso() "sso-up" path had no UI, so it's removed along with
  // its proxy routes to avoid implying a capability that isn't wired. The read
  // (sso() above) stays — the Fleet-page SSO summary drawer uses it.

  containment: () => get<Containment>("/fleet/containment", "Load containment"),
  providers: () => get<{ providers: Provider[] }>("/fleet/providers", "Load providers").then((r) => r.providers ?? []),
  addProvider: (body: ProviderInput) =>
    mutate("/fleet/providers", "POST", body, "Add provider").then((r) => r.json() as Promise<{ job_id: string }>),
  updateProvider: (handle: string, body: ProviderInput) =>
    mutate(`/fleet/providers/${encodeURIComponent(handle)}`, "PUT", body, "Update provider").then((r) => r.json() as Promise<{ job_id: string }>),
  removeProvider: (handle: string) =>
    mutate(`/fleet/providers/${encodeURIComponent(handle)}`, "DELETE", undefined, "Remove provider").then((r) => r.json() as Promise<{ job_id: string }>),

  // Access grants (SSH-key / OIDC-cert sandbox access). Synchronous — accessctl is
  // fast — so grant() returns the derived principal + fingerprint for the connect
  // handoff. Key + principal ride the request BODY (never a URL), incl. on DELETE.
  access: {
    list: (node?: string) =>
      // node is the worker's (private) IP; node_name is its display name — the Hosts
      // list identifies hosts by name, so the modal needs the name to show WHICH host
      // it targets. Optional: absent on older fleetd → the modal falls back to the IP.
      get<{ node: string; node_name?: string; tiers: string[]; grants: AccessGrant[] }>(
        `/fleet/access${node ? `?node=${encodeURIComponent(node)}` : ""}`, "Load access"),
    grant: (body: GrantInput) =>
      mutate("/fleet/access", "POST", body, "Grant access").then((r) => r.json() as Promise<GrantResult>),
    revoke: (principal: string, node?: string) =>
      mutate("/fleet/access", "DELETE", { principal, ...(node ? { node } : {}) }, "Revoke access")
        .then((r) => r.json() as Promise<{ ok: boolean; node: string; principal: string }>),
  },
};
